import React, { useContext, useMemo, useState } from 'react';
import { Tab, Tabs } from '@mui/material';
import styled from '@emotion/styled';
import TitleSearchLoadingCard from '@/components/OrderPlacement/OPTitleSearch/TitleSearchLoadingCard';
import OPViewingInventory from '@/components/OrderPlacement/OPStep2FileSelection/OPViewingInventory';
import OPViewingInventoryV2 from '@/components/OrderPlacement/OPStep2FileSelection/OPViewingInventoryV2';
import { OPViewingInventoryProps } from '@/components/OrderPlacement/OPStep2FileSelection/OPViewingInventory.types';
import { useAppDispatch } from '@/redux/hooks';
import { ConfigContext } from '@/contextAPI/configContext';
import { addOrRemoveInventoryUniqueSectionSelection } from '@/redux/slices/orderPlacementSlice';
import { InventoryFileEntity } from '@/redux/slices/slice_types';
import useFileType from '@/components/_hooks/useFileType';
import { useToast } from '@/components/_common/Toast';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OrderPlacementConfig } from '@/__configs/config_types';

type InventoryFilesListProps = {
  disableFileSelection: boolean;
};
const InventoryFilesList: React.FC<InventoryFilesListProps> = ({ disableFileSelection }) => {
  const isTitleV2 = useContext(ConfigContext)?.titleConfig?.v2;
  const orderPlacementEntityTypes = useConfigValue<OrderPlacementConfig['entityTypes']>(['orderPlacementConfig', 'entityTypes']);
  const dispatch = useAppDispatch();
  const [activeFileType, setActiveFileType] = useState<string>('all');
  const getFileGroup = useFileType();
  const { error } = useToast();

  const alternateEntityTypesFilter = useMemo(() => {
    return orderPlacementEntityTypes?.filter((type) => !['series', 'season'].includes(type));
  }, [orderPlacementEntityTypes]);

  const onClickFile = (file: InventoryFileEntity) => {
    if (disableFileSelection) {
      return;
    }

    const allowedSections = ['audio', 'subtitle'];
    const section = getFileGroup(file.file_type);
    if (!section || !allowedSections.includes(section)) {
      return error('Only audio, and subtitle files can be selected for previewing.');
    }

    dispatch(addOrRemoveInventoryUniqueSectionSelection({ file: { ...file, section }, sections: ['video', 'audio'] }));
  };

  const opViewingInventoryProps: OPViewingInventoryProps = {
    fileType: activeFileType,
    onClickFile
  };

  return (
    <Wrapper>
      <InventoryWrapper>
        <TitleSearchLoadingCard alternateEntityTypesFilter={alternateEntityTypesFilter}>
          <StyledTabs value={activeFileType} onChange={(_e, v) => setActiveFileType(v)} aria-label="inventory tabs">
            <Tab label="All" value="all" />
            <Tab label="Audio" value="audio" />
            <Tab label="Subtitle" value="subtitle" />
          </StyledTabs>
          {isTitleV2 ? <OPViewingInventoryV2 {...opViewingInventoryProps} /> : <OPViewingInventory {...opViewingInventoryProps} />}
        </TitleSearchLoadingCard>
      </InventoryWrapper>
    </Wrapper>
  );
};

export default InventoryFilesList;

const Wrapper = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-width: 300px;
  padding-top: ${({ theme }) => theme.spacing(7)};
`;

const InventoryWrapper = styled.div`
  flex-grow: 2;
  position: relative;
  padding: ${({ theme }) => theme.spacing(0, 3, 3, 3)};
  overflow-y: scroll;
  overflow-x: hidden;
`;

const StyledTabs = styled(Tabs)`
  && {
    & .MuiTab-root {
      padding: 0;
      min-width: 80px;
      margin: ${(props) => props.theme.spacing(0, 1, 0, 0)};
      line-height: 1.2;
      min-height: 38px;
      border-radius: ${(props) => props.theme.shape.borderRadius}px;
      background: ${(props) => props.theme.palette.background.default};

      ${(props) => `${props.theme.breakpoints.up('xl')} {
      padding: ${props.theme.spacing(0, 1)};
      min-width: 90px;
    }`};

      & > div {
        pointer-events: none;
      }
    }
    & .MuiTabs-indicator {
      display: none;
    }
    & .Mui-selected {
      background: ${(props) => props.theme.palette.primary.main};
      color: ${(props) => props.theme.palette.getContrastText(props.theme.palette.primary.main)};
    }
  }
`;
